import { Tab } from '@scuf/common';
import { useTranslation } from 'react-i18next';
import { COMPANY_MANAGER, USER_MANAGER, notificationAtom, BSLBR } from 'resources';
import { useSetRecoilState } from 'recoil';
import { bslbrCountriesAtom, bslbrPartnersAtom, bslbrPartsAtom } from 'resources/atoms/bslbrAtom';
import { useEffect, useState } from 'react';
import { CompanyDTO } from 'models/userManager/addCustomer';
import ProductSearch from './products/productSearch';
import Report from './report/report';
import InventorySearch from './inventory/inventorySearch';
import ContractSearch from './contracts/contractSearch';
import RepairSearch from './repair/repairSearch';
import QuotationSearch from './quotation/quotationSearch';
import useUser from 'helpers/hooks/useUser';
import useFetch from 'helpers/hooks/useFetch';
import HolidaySearch from './holiday/holidaySearch';
import i18n from 'i18n';
import { WelcomeMessageScreens } from 'models';

const SUPERADMIN = '1';
const ADMIN = '2';

interface ManualFile {
    id: number;
    displayName: string;
    storedFileName: string;
    section: 'HSL';
}

function BslbrPage() {
    const { t } = useTranslation(['bslbr']);
    const section = "msg-bslbr";
    const setNotification = useSetRecoilState(notificationAtom);
    const setPartners = useSetRecoilState(bslbrPartnersAtom);
    const setCountries = useSetRecoilState(bslbrCountriesAtom);
    const setParts = useSetRecoilState(bslbrPartsAtom);
    const { user: userDetails, getUser } = useUser();

    const [show, setShow] = useState(false);
    const [fileUrl, setFileUrl] = useState<string>();
    const [, fetchFiles] = useFetch<void, ManualFile>({
        url: `${USER_MANAGER.getIdPortalMensagem}?section=${section}&language=${i18n.language}`,
        method: 'GET'
    });

    useEffect(() => {
        if (userDetails && Object.keys(userDetails).length > 0) {
            loadFiles();
        }
    }, [userDetails]);

    const loadFiles = async (): Promise<void> => {
        try {
            if (hasSeenMessage === true) {
                return;
            }
            await fetchFiles(undefined, (resp) => {
                const { id } = resp.data ?? {};
                if (!id) {
                    return;
                }
                setFileUrl(`${USER_MANAGER.downloadManualFileById}?id=${id}`);
                setShow(true);
            });
        } catch (error: any) {
            if (error.status === 401) {
                await getUser();
            } else {
                console.error(t('manual.fetchFilesError'));
            }
        }
    };

    const [hasSeenMessage, setHasSeenMessage] = useState(() => {
        const storedValue = sessionStorage.getItem('welcomeMessageSeen');
        try {
            if (storedValue) {
                const welcomeMessageSeen: WelcomeMessageScreens =
                    JSON.parse(storedValue);

                return welcomeMessageSeen.bslbr === true;
            }
        } catch {
            return false;
        }
    });

    function handleMessage() {
        const storedValue = sessionStorage.getItem('welcomeMessageSeen');

        const welcomeMessageSeen = storedValue
            ? JSON.parse(storedValue)
            : {
                homepage: false,
                repair: false,
                spare: false,
                manufatura: false,
                hsl: false,
                bslbr: false
            };

        sessionStorage.setItem(
            'welcomeMessageSeen',
            JSON.stringify({
                ...welcomeMessageSeen,
                bslbr: true
            })
        );
        setHasSeenMessage(true);
        setShow(false);
    }

    const [, getPartners] = useFetch({
        url: COMPANY_MANAGER.getBslBrasilPartners,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [, getParts] = useFetch({
        url: BSLBR.getParts,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    useEffect(() => {
        getAllPartners();
        getAllParts();
    }, []);

    const getAllPartners = () => {
        getPartners(null, resp => {
            if (resp.data) {
                const partnersData = resp.data.map((partner: CompanyDTO) => ({
                    value: partner.name,
                    text: partner.name,
                    key: partner.id
                }));
                setPartners(partnersData);

                const uniqueCountries = Array.from(new Set(resp.data.map((partner: CompanyDTO) => String(partner.country))));
                const countriesData = uniqueCountries.map(country => ({
                    value: country as string,
                    text: country as string
                }));
                setCountries(countriesData);
            }
        });
    };

    const getAllParts = () => {
        getParts(null, (current: any) => {
            if (current.data.length) {
                setParts(current.data);
            } else {
                setNotification(old => ({ ...old, type: 'information', message: t('bslbr:translation.NotFoundParts') }));
            }
        });
    };

    return (
        <>
            {show && userDetails && Object.keys(userDetails).length > 0 && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}
                    onClick={() => handleMessage()}
                >
                    <img
                        style={{ maxWidth: '800px', maxHeight: '800px' }}
                        src={fileUrl}
                        onClick={e => e.stopPropagation()}
                        title="Mensagem Portal"
                    />
                </div>
            )}
            <div className="container">
                <Tab className="tab-with-divider">
                    <Tab.Pane title={t('translation.Repair')}>
                        <RepairSearch />
                    </Tab.Pane>
                    <Tab.Pane title={t('translation.Contracts')}>
                        <ContractSearch />
                    </Tab.Pane>
                    <Tab.Pane title={t('translation.Inventory')}>
                        <InventorySearch />
                    </Tab.Pane>
                    {userDetails?.role === SUPERADMIN || userDetails?.role === ADMIN ? (
                        <Tab.Pane title={t('translation.ProductManagement')}>
                            <ProductSearch />
                        </Tab.Pane>
                    ) : (
                        []
                    )}
                    <Tab.Pane title={t('translation.Quotation')}>
                        <QuotationSearch />
                    </Tab.Pane>
                    {userDetails?.role === SUPERADMIN || userDetails?.role === ADMIN ? (
                        <Tab.Pane title={t('translation.Report')}>
                            <Report />
                        </Tab.Pane>
                    ) : (
                        []
                    )}
                    {userDetails?.role === SUPERADMIN || userDetails?.role === ADMIN ? (
                        <Tab.Pane title={t('translation.HolidayManagement')}>
                            <HolidaySearch />
                        </Tab.Pane>
                    ) : (
                        []
                    )}
                </Tab>
            </div>
        </>
    );
}

export default BslbrPage;
